import {
  RPCMessage,
  IInjectTransport,
  IInjectTransportResponder,
  RPCMessageType,
  ListeningTicket,
  IRPCRequest,
  IRPCResponseWrapper,
  IRPCResult,
} from '@staffbar/types';

import {
  Serializer,
  Deserializer,
} from '../serialization';

import {
  randomIdentifier,
} from '../util';

class ResponseWrapper implements IRPCResponseWrapper {

  constructor({ injectTransport, requestId } : { injectTransport: InjectTransport, requestId: string }) {
    this._injectTransport = injectTransport;
    this._requestId = requestId;
  }

  private _injectTransport: InjectTransport;
  private _requestId: string;

  send(result: IRPCResult): void {
    this._injectTransport.publish(Object.assign({}, result, { id: this._requestId }), RPCMessageType.Response)
  }
}

export class InjectTransport implements IInjectTransport {

  constructor({ targetWindow, targetOrigin }:{ targetWindow: Window, targetOrigin: string }) {
    this._targetWindow = targetWindow 
    this._targetOrigin = targetOrigin
  }

  private _targetWindow: Window;
  private _targetOrigin: string;

  /**
   * In order for `window.removeEventListener` to work we need to pass the same
   * exact function that we used for `addListener`. This is a way to cache that
   * value because `.bind(this)` mutates the original `_onRawMessageRecevied`
   *
   * This also implicitly tracks state about if we're listening or not,
   * `undefined` meaning not listening
   *
   */
   private _listeneingRef: any = undefined

   startListening() {
     if (this._listeneingRef !== undefined) { return; }
     this._listeneingRef = this._onRawMessageReceived.bind(this)
     window.addEventListener('message', this._listeneingRef) 
   }
 
   stopListening() {
     if (this._listeneingRef === undefined) { return; }
     window.removeEventListener('message', this._listeneingRef)
     this._listeneingRef = undefined
   } 

  private _listeners: { [key: string]: IInjectTransportResponder} = {};

  public addListener(responder: (request: IRPCRequest, response: IRPCResponseWrapper) => void): ListeningTicket {
    let listeningTicket = randomIdentifier()
    this._listeners[listeningTicket] = responder
    return listeningTicket
  }

  public removeListener(listeningTicket: ListeningTicket) {
    if (this._listeners[listeningTicket]) {
      delete this._listeners[listeningTicket];
    }
  }

   private _onRawMessageReceived(event: MessageEvent) {

    // DO origin check here 
    const payload = event.data
    // TODO: Handle other types of messages here
    let deserializer = new Deserializer(payload)
    try {
      switch(deserializer.messageType()) {
        case RPCMessageType.Request: 
          this._onRequestMessageReceived(deserializer.toRequest())
      }
    } catch (error) {
      // Something about the message is invalid
      // console.log(`Unexpected error: ${error}`)
    }
  }

  private _onRequestMessageReceived(request: IRPCRequest) {
    let listnerKeys = Object.keys(this._listeners)
    for(let i = 0; i < listnerKeys.length; i++) {
      let listener = this._listeners[listnerKeys[i]];
      if (!listener) { continue; }
      let response = new ResponseWrapper({ injectTransport: this, requestId: request.id })
      listener(request, response)
    }
  }

  publish(message: RPCMessage, messageType?: RPCMessageType) {
    let serializedMessage = new Serializer(messageType || RPCMessageType.Publishment, message).serialize()
    this._targetWindow.postMessage(serializedMessage, this._targetOrigin)
  }
}
